import chunk from 'lodash/chunk';
import { useCallback, useContext } from 'react';

import { ALGOLIA_EVENT_MAX_OBJECT_IDS } from '@/integrations/algolia/const';
import { getIndexName } from '@/integrations/algolia/helpers';
import {
  carryCachedQueryID,
  forgetCachedQueryID,
} from '@/integrations/algolia/queryCache';
import { sendAlgoliaEvent } from '@/integrations/algolia/sendAlgoliaEvent';
import type {
  AlgoliaEvent,
  AlgoliaListPosition,
} from '@/integrations/algolia/types';
import { AlgoliaEventsNames } from '@/integrations/algolia/types';
import { captureError } from '@/lib/errors';
import { LocaleContext } from '@/providers/LocaleProvider/LocaleContext';
import { useAlgoliaInsightsEnabled } from '@/providers/ThirdPartyFlagsProvider/ThirdPartyFlagsContext';

/**
 * Flag-aware wrapper around sendAlgoliaEvent.
 * Use this hook in all React components instead of calling sendAlgoliaEvent
 * directly. It reads the current Algolia disable flag from context and silently
 * drops the event when analytics are disabled.
 */
export const useSendAlgoliaEvent = () => {
  const isEnabled = useAlgoliaInsightsEnabled();

  return useCallback(
    (event: AlgoliaEvent) => {
      if (!isEnabled) return;
      try {
        sendAlgoliaEvent(event);
      } catch (error) {
        // search-insights throws when an event is sent before `init` has run.
        captureError(error as Error, { extra: { event: event.event } });
      }
    },
    [isEnabled],
  );
};

/**
 * Drops the queryID an older search cached for a product reached from a list
 * that is not an Algolia result, so its next conversion is not attributed to
 * that search.
 */
export const useForgetAlgoliaQueryID = () => {
  const isEnabled = useAlgoliaInsightsEnabled();
  const locale = useContext(LocaleContext);

  return useCallback(
    (objectID: string) => {
      if (!isEnabled) return;
      forgetCachedQueryID(getIndexName('PRODUCT', locale), objectID);
    },
    [isEnabled, locale],
  );
};

/**
 * Sends a product click from a list outside InstantSearch, attributed to the
 * Algolia request (search, browse or Recommend) that returned the list when
 * its queryID is known.
 */
export const useSendClickedAlgoliaEvent = () => {
  const forgetAlgoliaQueryID = useForgetAlgoliaQueryID();
  const sendAlgoliaEvent = useSendAlgoliaEvent();
  const locale = useContext(LocaleContext);

  return useCallback(
    (objectID: string, listPosition?: AlgoliaListPosition) => {
      const indexName = getIndexName('PRODUCT', locale);

      if (listPosition?.queryID) {
        sendAlgoliaEvent({
          event: AlgoliaEventsNames.clickedObjectIDsAfterSearch,
          indexName,
          objectIDs: [objectID],
          queryID: listPosition.queryID,
          positions: [listPosition.position],
        });
        return;
      }

      forgetAlgoliaQueryID(objectID);
      sendAlgoliaEvent({
        event: AlgoliaEventsNames.clickedObjectIDs,
        indexName,
        objectIDs: [objectID],
      });
    },
    [forgetAlgoliaQueryID, locale, sendAlgoliaEvent],
  );
};

/**
 * Sends the products of a list shown to the user, in batches Algolia accepts,
 * and the facet filters (`attribute:value`) the list is filtered by.
 */
export const useSendViewedAlgoliaEvent = () => {
  const sendAlgoliaEvent = useSendAlgoliaEvent();
  const locale = useContext(LocaleContext);

  return useCallback(
    (
      objectIDs: string[],
      indexName = getIndexName('PRODUCT', locale),
      filters: string[] = [],
    ) => {
      // Lists rendered on page load mount before _app's effect initialises
      // search-insights; sending in the same tick throws and drops the event.
      setTimeout(() => {
        chunk(objectIDs, ALGOLIA_EVENT_MAX_OBJECT_IDS).forEach((batch) =>
          sendAlgoliaEvent({
            event: AlgoliaEventsNames.viewedObjectIDs,
            indexName,
            objectIDs: batch,
          }),
        );
        if (filters.length) {
          sendAlgoliaEvent({
            event: AlgoliaEventsNames.viewedFilters,
            indexName,
            filters,
          });
        }
      });
    },
    [locale, sendAlgoliaEvent],
  );
};

/**
 * Sends the add-to-cart conversion for one product. Without a queryID, the search
 * that led to the product is attributed by search-insights from the queryID it
 * cached when the product was clicked.
 */
export const useSendAddedToCartAlgoliaEvent = () => {
  const sendAlgoliaEvent = useSendAlgoliaEvent();
  const locale = useContext(LocaleContext);

  return useCallback(
    (objectID: string, price?: number, currency?: string, queryID?: string) => {
      sendAlgoliaEvent({
        event: AlgoliaEventsNames.addedToCartObjectIDs,
        indexName: getIndexName('PRODUCT', locale),
        objectIDs: [objectID],
        objectData: [{ queryID, price, quantity: 1 }],
        value: price,
        currency,
      });
    },
    [locale, sendAlgoliaEvent],
  );
};

/**
 * Before navigating from one variant of a product to another, gives the new
 * variant the queryID of the search that led to the current one.
 */
export const useCarryAlgoliaQueryID = () => {
  const isEnabled = useAlgoliaInsightsEnabled();
  const locale = useContext(LocaleContext);

  return useCallback(
    (fromObjectID: string, toObjectID: string) => {
      if (!isEnabled) return;
      carryCachedQueryID(
        getIndexName('PRODUCT', locale),
        fromObjectID,
        toObjectID,
      );
    },
    [isEnabled, locale],
  );
};
